import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTarih } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';

type Numune = {
  id: number; no: string; description: string; status: string;
  cari: { id: number; name: string };
  urun: { code: string; name: string } | null;
  sentAt: string | null; feedback: string | null; createdAt: string;
  bekleyenGun: number | null;
};
type Veri = { satirlar: Numune[]; ozet: { toplam: number; donen: number; donusYuzde: number } };

const DURUMLAR = ['HAZIRLANIYOR', 'GONDERILDI', 'ONAY', 'RET', 'SIPARISE_DONDU'] as const;
const ETIKET: Record<string, string> = {
  HAZIRLANIYOR: 'Hazırlanıyor', GONDERILDI: 'Gönderildi', ONAY: 'Onaylandı',
  RET: 'Reddedildi', SIPARISE_DONDU: 'Siparişe döndü',
};
const ROZET: Record<string, string> = {
  HAZIRLANIYOR: 'ccd-badge', GONDERILDI: 'ccd-badge ccd-badge-info',
  ONAY: 'ccd-badge ccd-badge-success', RET: 'ccd-badge ccd-badge-danger',
  SIPARISE_DONDU: 'ccd-badge ccd-badge-success',
};

/**
 * NUMUNE TAKİBİ (CRM eki 13.2) — "işletmenin en sık kaybettiği iz".
 * Numune → sipariş dönüşüm oranı burada ilk kez ölçülüyor.
 */
export default function Numune() {
  const { can } = useAuth();
  const [durum, setDurum] = useState('hepsi');
  const [veri, setVeri] = useState<Veri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [geriBildirim, setGeriBildirim] = useState<Record<number, string>>({});

  const yukle = useCallback(() => {
    api<Veri>(`/api/crm/numuneler?durum=${durum}`).then(setVeri)
      .catch((e) => setHata((e as Error).message));
  }, [durum]);
  useEffect(yukle, [yukle]);

  async function durumDegistir(id: number, status: string) {
    await api(`/api/crm/numune/${id}/durum`, {
      method: 'POST',
      body: JSON.stringify({ status, feedback: geriBildirim[id] || undefined }),
    }).catch((e) => setHata((e as Error).message));
    yukle();
  }

  return (
    <>
      <div>
        <h1 className="ccd-title">Numuneler</h1>
        <div className="ccd-meta">Numune → geri bildirim → onay/ret → teklif → sipariş</div>
      </div>

      {hata && (
        <div className="ccd-banner ccd-banner-danger">
          <span className="ccd-banner-icon">!</span><span>{hata}</span>
        </div>
      )}

      {veri && (
        <div className="ccd-grid ccd-grid-3">
          <div className="ccd-stat">
            <div className="ccd-stat-label">Toplam numune</div>
            <div className="ccd-stat-value">{veri.ozet.toplam}</div>
          </div>
          <div className="ccd-stat">
            <div className="ccd-stat-label">Siparişe dönen <Ipucu id="numune" /></div>
            <div className="ccd-stat-value">{veri.ozet.donen}</div>
          </div>
          <div className="ccd-stat">
            <div className="ccd-stat-label">Dönüşüm <Ipucu id="numune" /></div>
            <div className="ccd-stat-value">%{veri.ozet.donusYuzde}</div>
            <div className="ccd-stat-foot">bugüne kadarki tüm numuneler</div>
          </div>
        </div>
      )}

      <div className="ccd-tabs" role="tablist">
        <button role="tab" aria-selected={durum === 'hepsi'} className="ccd-tab"
                onClick={() => setDurum('hepsi')}>Tümü</button>
        {DURUMLAR.map((s) => (
          <button key={s} role="tab" aria-selected={durum === s} className="ccd-tab"
                  onClick={() => setDurum(s)}>{ETIKET[s]}</button>
        ))}
      </div>

      {!veri ? (
        hata ? null : <div className="ccd-inline-note">Yükleniyor…</div>
      ) : veri.satirlar.length === 0 ? (
        <div className="ccd-empty">
          Bu süzgeçte numune yok. Cari kartındaki "+ Numune" ile başlayın.
        </div>
      ) : (
        <div className="ccd-stack-sm">
          {veri.satirlar.map((n) => (
            <div key={n.id} className="ccd-card ccd-stack-sm">
              <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <b className="ccd-mono">{n.no}</b> {n.description}
                  <div className="ccd-meta">
                    <Link to={`/crm/${n.cari.id}`}>{n.cari.name}</Link>
                    {n.urun && ` · ${n.urun.code}`}
                    {n.sentAt && ` · gönderim ${formatTarih(n.sentAt)}`}
                    {n.bekleyenGun != null && n.status === 'GONDERILDI' && (
                      <span className={n.bekleyenGun > 14 ? ' ccd-kpi-down' : ''}>
                        {' '}· {n.bekleyenGun} gündür cevap yok
                      </span>
                    )}
                  </div>
                  {n.feedback && <div className="ccd-meta">“{n.feedback}”</div>}
                </div>
                <span className={ROZET[n.status] ?? 'ccd-badge'}>{ETIKET[n.status] ?? n.status}</span>
              </div>

              {can('quote.write') && n.status !== 'SIPARISE_DONDU' && (
                <div className="ccd-stack-sm">
                  {(n.status === 'GONDERILDI' || n.status === 'ONAY') && (
                    <input className="ccd-input" placeholder="Müşteri geri bildirimi…"
                           value={geriBildirim[n.id] ?? ''}
                           onChange={(e) => setGeriBildirim((g) => ({ ...g, [n.id]: e.target.value }))} />
                  )}
                  <div className="ccd-row" style={{ flexWrap: 'wrap' }}>
                    {n.status === 'HAZIRLANIYOR' && (
                      <button className="ccd-btn ccd-btn-primary ccd-btn-sm"
                              onClick={() => durumDegistir(n.id, 'GONDERILDI')}>Gönderildi</button>
                    )}
                    {n.status === 'GONDERILDI' && (
                      <>
                        <button className="ccd-btn ccd-btn-accent ccd-btn-sm"
                                onClick={() => durumDegistir(n.id, 'ONAY')}>Onaylandı</button>
                        <button className="ccd-btn ccd-btn-danger ccd-btn-sm"
                                onClick={() => durumDegistir(n.id, 'RET')}>Reddedildi</button>
                      </>
                    )}
                    {n.status === 'ONAY' && (
                      <button className="ccd-btn ccd-btn-primary ccd-btn-sm"
                              onClick={() => durumDegistir(n.id, 'SIPARISE_DONDU')}>
                        Siparişe döndü
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
